import { objectValueSchema, receiptSchema, resultSchema, type PlacedObject, type Mutation } from './content';
import type { AuthClient } from './auth';
import type { Connection } from './network';
import type { Viewport } from './viewport';

const el = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;
type Value = PlacedObject['value'];
interface Draft { base: PlacedObject; value: Value; lease: string; epoch: string; renewAt: number; previewAt: number }

/** Local selection and draft state. Only the accepted receipt is durable success. */
export class BuildingPanel {
  private draft: Draft | null = null;
  private busy = false;
  private renewing = false;
  private generation = 0;
  private epoch = '';
  private pending: { room: string; request: string } | null = null;
  private timer: ReturnType<typeof setInterval>;
  private visibility = (): void => { if (document.hidden) this.cancel(); };
  constructor(private connection: Connection, private auth: AuthClient, private viewport: Viewport, showTools: () => void = () => {}) {
    el('edit-object').addEventListener('click', () => void this.run(() => this.acquire()));
    el('save-object').addEventListener('click', () => void this.run(() => this.commit(false)));
    el('cancel-edit').addEventListener('click', () => this.cancel());
    el('check-edit').addEventListener('click', () => void this.run(() => this.checkSave()));
    el('move-fixture').addEventListener('click', () => void this.run(async () => {
      await this.acquire(); if (!this.draft) return;
      this.change({ ...this.draft.value, position: { ...this.draft.value.position, x: Math.min(11.5, this.draft.value.position.x + 1) } }); await this.commit(false);
    }));
    el('delete-fixture').addEventListener('click', () => void this.run(async () => { await this.acquire(); if (this.draft) await this.commit(true); }));
    el('object-select').addEventListener('change', () => { this.cancel(); this.render(); });
    for (const axis of ['x', 'y', 'z'] as const) el(`build-${axis}`).addEventListener('input', () => {
      if (this.draft && !this.busy) this.change({ ...this.draft.value, position: { ...this.draft.value.position, [axis]: el<HTMLInputElement>(`build-${axis}`).valueAsNumber } }, false);
    });
    for (const id of ['build-mode', 'build-snap']) el(id).addEventListener('change', () => this.mode());
    viewport.onSelect = id => { if (this.busy) return; this.cancel(); el<HTMLSelectElement>('object-select').value = id; this.render(); showTools(); };
    viewport.onTransform = (p, q, s) => {
      if (this.draft && !this.busy) this.change({ ...this.draft.value, position: { x: p.x, y: p.y, z: p.z }, rotation: [q.x, q.y, q.z, q.w], scale: { x: s.x, y: s.y, z: s.z } });
    };
    this.mode(); this.timer = setInterval(() => this.pulse(), 250); document.addEventListener('visibilitychange', this.visibility);
  }
  private message(text: string): void { el('build-status').textContent = text; }
  private selected(): PlacedObject | undefined { return this.connection.state.content?.objects.find(o => o.id === el<HTMLSelectElement>('object-select').value); }
  editUnavailable(id: string): string | undefined {
    const object = this.connection.state.content?.objects.find(item => item.id === id), permissions = this.connection.state.permissions;
    if (!object) return 'This object is no longer available.';
    if (this.connection.state.content?.room.environmentData?.objectIds.includes(id)) return 'This is part of the room environment. Import a new package to replace it.';
    if (!permissions.canBuild || (!permissions.canManage && object.ownerId !== this.auth.state?.user?.id)) return 'You do not have permission to edit this object.';
    if (this.busy) return 'An edit operation is already in progress.';
    if (this.pending) return 'Resolve the previous save outcome first.';
    const held = this.connection.edits.get(id);
    if (held && held.holderId !== this.connection.state.selfId && held.expiresMs > this.connection.serverTime()) return 'Another visitor is editing this object.';
  }
  edit(id: string): void {
    const unavailable = this.editUnavailable(id);
    if (unavailable) { this.message(unavailable); return; }
    if (this.selected()?.id !== id) { this.cancel(); el<HTMLSelectElement>('object-select').value = id; this.render(); }
    void this.run(() => this.acquire());
  }
  private mode(): void { this.viewport.editMode(el<HTMLSelectElement>('build-mode').value as 'translate' | 'rotate' | 'scale', el<HTMLInputElement>('build-snap').checked); }
  private async run(work: () => Promise<void>): Promise<void> {
    if (this.busy) return;
    this.busy = true; const generation = this.generation; this.render();
    try { await work(); }
    catch (error) { if (generation === this.generation) { this.cancel(); this.message(error instanceof Error ? error.message : 'Edit failed'); } }
    finally { if (generation === this.generation) this.busy = false; this.render(); }
  }
  private async acquire(): Promise<void> {
    const object = this.selected(); if (!object) throw new Error('Select an object');
    if (this.draft?.base.id === object.id) return;
    const generation = this.generation, epoch = this.connection.state.epoch;
    const lease = await this.connection.editLease('acquire', object.id, object.revision);
    if (generation !== this.generation || epoch !== this.connection.state.epoch) {
      if (epoch === this.connection.state.epoch) void this.connection.editLease('release', object.id, object.revision, lease).catch(() => {});
      return;
    }
    this.draft = { base: structuredClone(object), value: structuredClone(object.value), lease, epoch, renewAt: performance.now() + 3000, previewAt: 0 };
    this.viewport.editObject(this.draft.value); this.fields(); this.message('Editing · drag a gizmo or enter a position, then Save transform.');
  }
  private fields(): void {
    if (!this.draft) return;
    for (const axis of ['x', 'y', 'z'] as const) el<HTMLInputElement>(`build-${axis}`).value = String(Number(this.draft.value.position[axis].toFixed(3)));
  }
  private change(value: Value, updateFields = true): void {
    if (!this.draft) return;
    const result = objectValueSchema.safeParse(value);
    if (!result.success) { this.viewport.setDraft(this.draft.value); this.message('Outside build limits: position ±11.5m X/Z, 0–10m Y; scale 0.1–4.'); return; }
    this.draft.value = result.data; this.viewport.setDraft(result.data); if (updateFields) this.fields();
  }
  private pulse(): void {
    const draft = this.draft; if (!draft || this.busy || document.hidden) return;
    const now = performance.now();
    if (now >= draft.previewAt) { draft.previewAt = now + 250; this.connection.preview(draft.base.id, draft.lease, draft.value); }
    if (now >= draft.renewAt && !this.renewing) {
      this.renewing = true; draft.renewAt = now + 3000;
      void this.connection.editLease('renew', draft.base.id, draft.base.revision, draft.lease).catch(error => {
        if (this.draft === draft) { this.cancel(); this.message(error instanceof Error ? error.message : 'Lease lost'); }
      }).finally(() => { this.renewing = false; });
    }
  }
  private async commit(remove: boolean): Promise<void> {
    const draft = this.draft; if (!draft) return;
    const generation = this.generation, room = this.connection.roomID;
    const mutation: Mutation = remove
      ? { requestId: crypto.randomUUID(), kind: 'object.delete', objectId: draft.base.id, expectedRevision: draft.base.revision }
      : { requestId: crypto.randomUUID(), kind: 'object.update', objectId: draft.base.id, expectedRevision: draft.base.revision, object: draft.value };
    this.pending = { room, request: mutation.requestId }; this.message('Saving…');
    try {
      const response = await this.auth.post(`/api/rooms/${room}/commands`, mutation, { 'X-Offworld-Epoch': draft.epoch, 'X-Offworld-Lease': draft.lease });
      const body: unknown = await response.json(); if (generation !== this.generation) return;
      const parsed = resultSchema.safeParse(body);
      if (!parsed.success) {
        const code = typeof body === 'object' && body !== null && 'error' in body ? String(body.error) : 'outcome_unknown';
        if (['lease_lost', 'object_leased', 'forbidden', 'unauthorized', 'invalid_object'].includes(code)) this.pending = null;
        this.cancel(); this.message(`${code.replaceAll('_', ' ')}${this.pending ? ' — check save outcome.' : ''}`); return;
      }
      this.pending = null; this.cancel();
      this.message(parsed.data.receipt.status === 'applied' ? `Saved · revision ${parsed.data.receipt.revision}` : `Not saved: ${parsed.data.receipt.status}`);
    } catch {
      if (generation === this.generation) { this.cancel(); this.message('Outcome unknown — check save outcome, or reconnect to resnapshot.'); }
    }
  }
  private async checkSave(): Promise<void> {
    const pending = this.pending; if (!pending) return;
    const generation = this.generation;
    const response = await worldFetch(`/api/rooms/${pending.room}/receipts/${pending.request}`, { signal: AbortSignal.timeout(6000) });
    if (generation !== this.generation) return;
    if (!response.ok) { this.message('No receipt available yet — check again or reconnect to resnapshot.'); return; }
    const receipt = receiptSchema.parse(await response.json()); if (generation !== this.generation) return;
    this.pending = null; this.message(`Save outcome: ${receipt.status} · revision ${receipt.revision}`);
  }
  cancel(): void {
    const draft = this.draft; this.draft = null; this.generation++; this.busy = false; this.viewport.editObject(null);
    if (draft) this.message('Edit cancelled · restored accepted transform.');
    if (draft && draft.epoch === this.connection.state.epoch) void this.connection.editLease('release', draft.base.id, draft.base.revision, draft.lease).catch(() => {});
    this.render();
  }
  presentation(): PlacedObject[] {
    const now = this.connection.serverTime();
    return (this.connection.state.content?.objects ?? []).map(object => {
      const state = this.connection.edits.get(object.id);
      const preview = state && state.revision === object.revision && state.expiresMs > now && state.previewExpiresMs > now ? state.preview : null;
      const value = this.draft?.base.id === object.id && this.draft.base.revision === object.revision ? this.draft.value : preview;
      return value ? { ...object, value } : object;
    });
  }
  render(): void {
    if (this.epoch !== this.connection.state.epoch) { this.epoch = this.connection.state.epoch; this.pending = null; this.cancel(); this.message('Decorative objects · double-click an object to select.'); }
    const object = this.selected(), draft = this.draft;
    if (draft && !this.busy) {
      const state = this.connection.edits.get(draft.base.id);
      if (!state || state.holderId !== this.connection.state.selfId || state.expiresMs <= this.connection.serverTime() || object?.id !== draft.base.id || object.revision !== draft.base.revision) { this.cancel(); this.message('Lease lost — restored accepted transform.'); }
    }
    this.viewport.selectObject(object && !this.connection.state.content?.room.environmentData?.objectIds.includes(object.id) ? object.id : '');
    const held = object && this.connection.edits.get(object.id);
    const locked = !!held && held.holderId !== this.connection.state.selfId && held.expiresMs > this.connection.serverTime();
    for (const id of ['edit-object', 'move-fixture', 'delete-fixture']) el<HTMLButtonElement>(id).disabled = !object || !!this.editUnavailable(object.id);
    el<HTMLButtonElement>('save-object').disabled = this.busy || !this.draft || !!this.pending;
    el<HTMLButtonElement>('cancel-edit').disabled = this.busy || !this.draft;
    el<HTMLButtonElement>('check-edit').disabled = this.busy || !this.pending;
    el<HTMLSelectElement>('object-select').disabled = this.busy;
    el('build-tools').hidden = !this.auth.state?.user;
    el('build-holder').textContent = locked ? 'Selected object is being edited by another connection.' : '';
    for (const axis of ['x', 'y', 'z']) el<HTMLInputElement>(`build-${axis}`).disabled = this.busy || !this.draft;
  }
  dispose(): void { clearInterval(this.timer); document.removeEventListener('visibilitychange', this.visibility); this.cancel(); this.viewport.onSelect = () => {}; this.viewport.onTransform = () => {}; }
}
import {worldFetch} from './world-transport';
